import clsx from "clsx"

import { TypeIcon } from "@/components/Standup"
import type { WeeklyDefects as WeeklyDefectsData, WeeklyDefectsRowKey } from "@/types/sprint-report"

interface Props {
  data: WeeklyDefectsData
}

/* --------------------------------------------------------------- rows */

const ROWS: { key: WeeklyDefectsRowKey; label: string; type: string; priBg: string; pri: string }[] = [
  { key: "p1_bugs", label: "P1 Bugs", type: "Bug", priBg: "bg-priority-p1", pri: "P1" },
  { key: "p2_bugs", label: "P2 Bugs", type: "Bug", priBg: "bg-priority-p2", pri: "P2" },
  { key: "p1_pbis", label: "P1 PBIs", type: "Product Backlog Item", priBg: "bg-priority-p1", pri: "P1" },
]

/** A week-over-week move worth calling out: |Δ| ≥ 5, or ≥ 30% off a base ≥ 10. */
function isSignificant(prev: number, next: number): boolean {
  const d = Math.abs(next - prev)
  return d >= 5 || (prev >= 10 && d / prev >= 0.3)
}

function Delta({ from, to, invert = false }: { from: number; to: number; invert?: boolean }) {
  const d = to - from
  if (d === 0) return <span className="num text-slate-400">±0</span>
  // For defect counts, DOWN is the win.
  const good = invert ? d > 0 : d < 0
  return (
    <span className={clsx("num font-semibold", good ? "text-win-deep" : "text-red-700")}>
      {d > 0 ? "▲" : "▼"} {d > 0 ? `+${d}` : d}
    </span>
  )
}

/** Gross weekly flows: "+added" (inflow, red) / "−fixed" (outflow, green). */
function Flows({ added, fixed }: { added: number; fixed: number }) {
  if (added === 0 && fixed === 0) return <span className="num text-slate-300">·</span>
  return (
    <span className="num whitespace-nowrap">
      <span className={added > 0 ? "font-semibold text-red-700" : "text-slate-300"}>
        +{added}
      </span>
      <span className="text-slate-300"> / </span>
      <span className={fixed > 0 ? "font-semibold text-win-deep" : "text-slate-300"}>
        −{fixed}
      </span>
    </span>
  )
}

function pct(from: number, to: number): string {
  if (from === 0) return to === 0 ? "0%" : "n/a"
  return `${Math.round(((to - from) / from) * 100)}%`
}

/* --------------------------------------------------------------- view */

export function WeeklyDefects({ data }: Props) {
  const { weeks, rows, flows, methodology } = data
  const n = weeks.length
  if (n === 0) {
    return <p className="p-6 text-center text-sm text-slate-500">No completed weeks in this snapshot.</p>
  }
  const first = weeks[0]
  const last = weeks[n - 1]

  // The comparison anchor is the regression-1 peak, a couple of columns in —
  // NOT the leftmost week (which is the start of the regression ramp). Deltas
  // measure recovery from that peak.
  const baseIdx = Math.min(Math.max(data.baseline_index ?? 0, 0), n - 1)
  const baseWeek = weeks[baseIdx]
  const baseLabel = data.baseline_label ?? baseWeek.label

  const base = (k: WeeklyDefectsRowKey) => rows[k][baseIdx] ?? 0
  const newest = (k: WeeklyDefectsRowKey) => rows[k][n - 1] ?? 0

  // Per-week totals across all three categories. Rows are disjoint (a work
  // item is only ever one of P1 Bug / P2 Bug / P1 PBI), so summing counts and
  // flows column-by-column is exact — no double-counting.
  const totalCounts = weeks.map((_, i) => ROWS.reduce((s, r) => s + (rows[r.key][i] ?? 0), 0))
  const totalAdded = weeks.map((_, i) =>
    ROWS.reduce((s, r) => s + (flows?.[r.key].added[i] ?? 0), 0),
  )
  const totalFixed = weeks.map((_, i) =>
    ROWS.reduce((s, r) => s + (flows?.[r.key].fixed[i] ?? 0), 0),
  )

  const p1Workload = newest("p1_bugs") + newest("p1_pbis")
  const p2Workload = newest("p2_bugs")
  const totalDelta = ROWS.reduce((s, r) => s + (newest(r.key) - base(r.key)), 0)

  // Notable week-over-week moves, per row, oldest → newest — with the gross
  // added/fixed flows behind each net move.
  const moves = ROWS.flatMap((r) =>
    rows[r.key]
      .slice(1)
      .map((v, i) => ({
        row: r.label,
        week: weeks[i + 1].label,
        from: rows[r.key][i],
        to: v,
        added: flows?.[r.key].added[i + 1] ?? 0,
        fixed: flows?.[r.key].fixed[i + 1] ?? 0,
      }))
      .filter((m) => isSignificant(m.from, m.to)),
  )

  // Direction over the most recent 4 completed weeks — drives the recommendation.
  const back = Math.min(4, n - 1)
  const recentDelta = ROWS.reduce(
    (s, r) => s + (newest(r.key) - (rows[r.key][n - 1 - back] ?? 0)),
    0,
  )
  const falling = recentDelta < 0
  const flat = recentDelta === 0

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-bold text-ink">Weekly Defect Trend</h2>
          <p className="text-xs text-slate-500">
            Open P1/P2 Bugs and P1 PBIs at the end of every completed week from{" "}
            <strong>{first.label}</strong> (the start of the regression-1 window) through the
            current week to date ({first.start_date} → {last.end_date}), reconstructed from ADO
            revision history: each completed week shows what was actually open <em>then</em>, not
            today. Deltas are measured against <strong>{baseLabel}</strong> — the regression-1
            peak — not the leftmost column. The window is pinned and grows a column each week
            rather than rolling.
          </p>
        </div>
        <span className="win-pill">auto-refreshes every 15 min</span>
      </div>

      {/* KPI strip: newest week vs the regression-1 peak baseline */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {ROWS.map((r) => (
          <div key={r.key} className="rounded-xl border border-slate-200/70 bg-white/70 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <TypeIcon type={r.type} />
              {r.label}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="num text-2xl font-bold text-ink">{newest(r.key)}</span>
              <Delta from={base(r.key)} to={newest(r.key)} />
              <span className="text-[10px] text-slate-400">
                vs {baseLabel}
                {last.is_current && " · to date"}
              </span>
            </div>
          </div>
        ))}
        <div className="rounded-xl border border-slate-200/70 bg-white/70 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            All three rows
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="num text-2xl font-bold text-ink">
              {ROWS.reduce((s, r) => s + newest(r.key), 0)}
            </span>
            <Delta from={ROWS.reduce((s, r) => s + base(r.key), 0)} to={ROWS.reduce((s, r) => s + newest(r.key), 0)} />
            <span className="text-[10px] text-slate-400">vs {baseLabel}</span>
          </div>
        </div>
      </div>

      {/* The table */}
      <div className="overflow-x-auto rounded-xl border border-slate-200/80">
        <table className="data-table">
          <thead>
            <tr>
              <th>Work Item Category</th>
              {weeks.map((w) => (
                <th
                  key={w.label}
                  className={clsx("!text-right", w.is_baseline && "bg-priority-p1/[0.12]")}
                  title={
                    w.is_baseline
                      ? `${w.start_date} → ${w.end_date} · regression-1 peak — comparison baseline`
                      : w.is_current
                        ? `${w.start_date} → today (in progress; counts as of ${w.as_of})`
                        : `${w.start_date} → ${w.end_date} (as of ${w.as_of})`
                  }
                >
                  {w.label}
                  {w.is_current && <span className="ml-0.5 align-super text-[8px]">now</span>}
                  {w.is_baseline && (
                    <span className="ml-0.5 align-super text-[8px] text-priority-p1">base</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.key}>
                <td>
                  <span className="flex items-center gap-1.5 whitespace-nowrap font-semibold text-ink">
                    <TypeIcon type={r.type} />
                    <span
                      className={clsx(
                        "inline-block rounded px-1.5 py-0.5 text-[10px] font-bold leading-none text-white",
                        r.priBg,
                      )}
                    >
                      {r.pri}
                    </span>
                    {r.label}
                  </span>
                </td>
                {rows[r.key].map((v, i) => (
                  <td
                    key={weeks[i].label}
                    className={clsx(
                      "num text-right",
                      i === n - 1 && "bg-headerSub/[0.07] font-bold text-ink",
                      weeks[i].is_baseline && "bg-priority-p1/[0.06] font-semibold text-ink",
                    )}
                  >
                    {/* Count + the gross flows behind it: how many entered
                        (+red) and left (−green) the row during that week —
                        churn a net delta would hide. */}
                    <span className="inline-flex flex-col items-end leading-tight">
                      <span>{v}</span>
                      {flows && (
                        <span className="text-[9px] font-normal">
                          <Flows
                            added={flows[r.key].added[i] ?? 0}
                            fixed={flows[r.key].fixed[i] ?? 0}
                          />
                        </span>
                      )}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300">
              <td>
                <span className="flex items-center gap-1.5 whitespace-nowrap font-bold text-ink">
                  Σ All open defects
                </span>
              </td>
              {totalCounts.map((v, i) => (
                <td
                  key={weeks[i].label}
                  className={clsx(
                    "num text-right font-bold text-ink",
                    i === n - 1 && "bg-headerSub/[0.1]",
                    weeks[i].is_baseline && "bg-priority-p1/[0.1]",
                  )}
                >
                  <span className="inline-flex flex-col items-end leading-tight">
                    <span>{v}</span>
                    {flows && (
                      <span className="text-[9px] font-normal">
                        <Flows added={totalAdded[i]} fixed={totalFixed[i]} />
                      </span>
                    )}
                  </span>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      {flows && (
        <p className="!mt-1.5 text-[10px] text-slate-400">
          Each cell: open count at week end, then that week&apos;s gross flows —{" "}
          <span className="num font-semibold text-red-700">+added</span> /{" "}
          <span className="num font-semibold text-win-deep">−fixed</span>. Items opened and
          fixed inside the same week count in both, so a flat week can still show real churn.
        </p>
      )}

      {/* Analysis */}
      <div className="grid gap-3 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200/70 bg-white/70 p-4">
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            1 · Change {baseLabel} (regression-1 peak) → {last.label}
            {last.is_current && " (to date)"}
          </h3>
          <ul className="mt-2 space-y-1.5 text-xs text-slate-700">
            {ROWS.map((r) => (
              <li key={r.key} className="flex items-center gap-2">
                <span className="w-16 font-semibold">{r.label}</span>
                <span className="num text-slate-500">
                  {base(r.key)} → {newest(r.key)}
                </span>
                <Delta from={base(r.key)} to={newest(r.key)} />
                <span className="text-slate-400">({pct(base(r.key), newest(r.key))})</span>
                {flows && (
                  <span className="ml-auto text-[10px] text-slate-400">
                    Σ since {baseLabel}{" "}
                    <Flows
                      added={flows[r.key].added.slice(baseIdx + 1).reduce((s, x) => s + x, 0)}
                      fixed={flows[r.key].fixed.slice(baseIdx + 1).reduce((s, x) => s + x, 0)}
                    />
                  </span>
                )}
              </li>
            ))}
            <li className="flex items-center gap-2 border-t border-slate-100 pt-1.5">
              <span className="w-16 font-semibold">Combined</span>
              <span className="num text-slate-500">
                {ROWS.reduce((s, r) => s + base(r.key), 0)} →{" "}
                {ROWS.reduce((s, r) => s + newest(r.key), 0)}
              </span>
              <Delta
                from={ROWS.reduce((s, r) => s + base(r.key), 0)}
                to={ROWS.reduce((s, r) => s + newest(r.key), 0)}
              />
            </li>
          </ul>
        </section>

        <section className="rounded-xl border border-slate-200/70 bg-white/70 p-4">
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            2 · Significant week-over-week moves
          </h3>
          {moves.length === 0 ? (
            <p className="mt-2 text-xs text-slate-500">
              No week-over-week move of ≥5 items (or ≥30%) in the window.
            </p>
          ) : (
            <ul className="mt-2 space-y-1.5 text-xs text-slate-700">
              {moves.map((m, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="num w-9 text-slate-400">{m.week}</span>
                  <span className="w-16 font-semibold">{m.row}</span>
                  <span className="num text-slate-500">
                    {m.from} → {m.to}
                  </span>
                  <Delta from={m.from} to={m.to} />
                  {flows && (
                    <span className="text-[10px] text-slate-400">
                      (<Flows added={m.added} fixed={m.fixed} />)
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-xl border border-slate-200/70 bg-white/70 p-4">
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            3 · Current workload ({last.is_current ? `${last.label} to date` : `end of ${last.label}`})
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-slate-700">
            <strong className="text-ink">P1:</strong>{" "}
            <span className="num">{p1Workload}</span> open items ({newest("p1_bugs")} Bugs +{" "}
            {newest("p1_pbis")} PBIs) · <strong className="text-ink">P2:</strong>{" "}
            <span className="num">{p2Workload}</span> open Bugs.
          </p>
        </section>

        <section
          className={clsx(
            "rounded-xl border p-4",
            falling || flat ? "border-win/30 bg-win-light/40" : "border-warn/30 bg-warn-light/60",
          )}
        >
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            4 · Recommendation
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-slate-700">
            {falling ? (
              <>
                Open defects fell by <span className="num font-semibold">{Math.abs(recentDelta)}</span>{" "}
                over the last {back} weeks{totalDelta < 0 && (
                  <> (and {Math.abs(totalDelta)} since the {baseLabel} peak)</>
                )}
                . Current fixing capacity is keeping ahead of inflow — hold the present allocation
                and keep prioritizing the remaining <span className="num font-semibold">{p1Workload}</span>{" "}
                P1s to zero before pulling capacity away.
              </>
            ) : flat ? (
              <>
                Open defects are flat over the last {back} weeks. Inflow and fixing are balanced;
                to actually burn the <span className="num font-semibold">{p1Workload}</span> open P1s
                down, add targeted bug-fixing capacity or re-prioritize P2s behind P1s.
              </>
            ) : (
              <>
                Open defects rose by <span className="num font-semibold">{recentDelta}</span> over
                the last {back} weeks — inflow is outpacing fixes. Recommend adding bug-fixing
                capacity (or pausing feature work) until the{" "}
                <span className="num font-semibold">{p1Workload}</span> open P1s trend down again.
              </>
            )}
          </p>
        </section>
      </div>

      {/* Data quality + methodology */}
      <details className="rounded-xl border border-slate-200/70 bg-white/60 p-4 text-xs text-slate-600 [&[open]>summary]:mb-3">
        <summary className="cursor-pointer select-none text-[11px] font-bold uppercase tracking-wider text-slate-400 hover:text-ink">
          5 · Data quality, assumptions &amp; methodology (how these numbers are made)
        </summary>
        <div className="space-y-3">
          <div>
            <h4 className="font-semibold text-ink">Data quality &amp; assumptions</h4>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              <li>
                The ADO <code>Microsoft.VSTS.Common.Priority</code> field is unusable here — nearly
                every item carries the default value (2) regardless of real priority. Priority is
                parsed from title tags (<code>[P1]</code>, <code>[P1-A]</code>, <code>[P2]</code>…)
                with a tag fallback, the same rule as every other tab.
              </li>
              <li>
                Items with no parsable priority tag are excluded (they are also outside the report
                scope on the other tabs).
              </li>
              <li>
                History-accurate: an item retitled from <code>[P2]</code> to <code>[P1]</code> counts
                as P2 in the weeks before the change — each week uses the title/state held at that
                week&apos;s end (WIQL <code>ASOF</code> + as-of revisions).
              </li>
              <li>
                &ldquo;Resolved&rdquo; does not exist in the CAT-OS process template; it is excluded
                defensively alongside Closed / Done / Removed.
              </li>
              <li>
                Scope is the whole <strong>{methodology.project}</strong> project (consistent with
                the rest of this report), weeks are ISO Mon–Sun in America/Toronto, and each work
                item is counted at most once per week (single priority + disjoint types).
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-ink">Filters used</h4>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              <li>
                <strong>Date range:</strong> {first.start_date} → {last.end_date} (
                {weeks.length - 1} completed weeks + the current week); completed weeks counted
                at Sunday 23:59:59 local, the current week as of the last refresh.
              </li>
              <li>
                <strong>Work-item types:</strong> {methodology.work_item_types.join(", ")}
              </li>
              <li>
                <strong>Excluded states:</strong> {methodology.excluded_states.join(", ")}
              </li>
              <li>
                <strong>Rows:</strong> {ROWS.map((r) => `${r.label} — ${methodology.rows[r.key]}`).join(" · ")}
              </li>
              {methodology.flows && (
                <li>
                  <strong>Added / Fixed:</strong> {methodology.flows}
                </li>
              )}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-ink">Query (one per week end)</h4>
            <pre className="mt-1 overflow-x-auto rounded-lg bg-ink p-3 font-mono text-[10px] leading-relaxed text-slate-200">
              {methodology.wiql}
            </pre>
            <p className="mt-2">
              <strong>Rerun:</strong> automatic — the backend recomputes this on its 15-minute
              refresh (completed weeks are cached; the window rolls forward every Monday). Manual
              rerun: <code>POST /api/refresh</code>.
            </p>
          </div>
        </div>
      </details>
    </div>
  )
}
